import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import {
  FarmOut,
  FarmOutAction,
  FarmOutNegotiation,
  farmOutAPI,
  farmOutError,
  farmOutStateLabel,
} from '../../services/farmOutApi';

export default function FarmOutCard({ offer }: { offer: FarmOut }) {
  const client = useQueryClient();
  const [intent, setIntent] = useState<{
    action: FarmOutAction;
    label: string;
    recipientCompanyId?: string;
    negotiation?: FarmOutNegotiation;
    requiresAmount?: boolean;
    fixedAmountCents?: number;
    requestId?: string;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const mutation = useMutation(
    (selected: NonNullable<typeof intent>) =>
      farmOutAPI.act(offer.id, selected.action, {
        reason: reason.trim() || undefined,
        recipientCompanyId: selected.recipientCompanyId,
        negotiationId: selected.negotiation?.id,
        expectedVersion: selected.negotiation?.version,
        requestId: selected.requestId,
        amountCents:
          selected.fixedAmountCents ??
          (selected.requiresAmount
            ? Math.round(Number(amount) * 100)
            : undefined),
        note: note.trim() || undefined,
      }),
    {
      onSuccess: async () => {
        setIntent(null);
        setReason('');
        setAmount('');
        setNote('');
        await Promise.all(
          [
            'farm-outs',
            'farm-out-context',
            'farm-out-detail',
            'admin-dispatch',
          ].map((key) => client.invalidateQueries(key))
        );
      },
    }
  );
  const date = new Date(offer.startAt).toLocaleString(undefined, {
    timeZone: offer.timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const needsReason =
    intent && ['withdraw', 'cancel', 'attention'].includes(intent.action);
  const proposalAction =
    intent && ['propose', 'counter'].includes(intent.action);
  const actionButton = (
    action: FarmOutAction,
    label: string,
    recipientCompanyId?: string
  ) => (
    <button
      type="button"
      className="btn-secondary"
      disabled={mutation.isLoading}
      onClick={() => {
        mutation.reset();
        setReason('');
        setAmount('');
        setNote('');
        setIntent({ action, label, recipientCompanyId });
      }}
    >
      {label}
    </button>
  );
  const negotiationButton = (
    action: FarmOutAction,
    label: string,
    negotiation?: FarmOutNegotiation,
    options: { requiresAmount?: boolean; fixedAmountCents?: number } = {}
  ) => (
    <button
      type="button"
      className="btn-secondary"
      disabled={mutation.isLoading}
      onClick={() => {
        mutation.reset();
        setReason('');
        setAmount(
          options.requiresAmount && negotiation?.currentAmountCents
            ? (negotiation.currentAmountCents / 100).toFixed(2)
            : ''
        );
        setNote('');
        setIntent({
          action,
          label,
          negotiation,
          requestId: ['propose', 'counter'].includes(action)
            ? crypto.randomUUID()
            : undefined,
          ...options,
        });
      }}
    >
      {label}
    </button>
  );
  const money = (cents: number, currency = offer.currency) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(cents / 100);
  return (
    <article
      className="admin-subpanel rounded-card p-5 space-y-4"
      aria-label={`${offer.pickupArea} to ${offer.dropoffArea}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-on-surface-variant">
            {offer.originName} · {farmOutStateLabel(offer)}
          </p>
          <h2 className="text-xl font-semibold">
            {offer.pickupArea} → {offer.dropoffArea}
          </h2>
          <p>
            Pickup {date} · Ends{' '}
            {new Date(offer.endAt).toLocaleString(undefined, {
              timeZone: offer.timezone,
              dateStyle: 'medium',
              timeStyle: 'short',
            })}{' '}
            <span className="text-sm text-on-surface-variant">
              ({offer.timezone})
            </span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-xl font-semibold">
            {money(offer.agreedAmountCents ?? offer.amountCents)}
          </p>
          <p className="text-xs">
            {offer.agreedAmountCents ? 'Agreed amount' : 'Listed amount'} ·{' '}
            {offer.currency}
          </p>
        </div>
      </div>
      <p className="whitespace-pre-wrap">{offer.requirements}</p>
      {offer.publicNotes && (
        <p className="text-sm whitespace-pre-wrap">{offer.publicNotes}</p>
      )}
      <section className="text-sm" aria-label="Price terms">
        <h3 className="font-semibold">Amount includes</h3>
        <p>
          Base reservation service
          {offer.priceTerms?.includesTolls ? ' · tolls' : ''}
          {offer.priceTerms?.includesParking
            ? ' · parking and airport fees'
            : ''}
          {offer.priceTerms?.includesGratuity ? ' · gratuity' : ''}
          {offer.priceTerms?.includedWaitMinutes
            ? ` · ${offer.priceTerms.includedWaitMinutes} minutes waiting time`
            : ''}
        </p>
        <p className="text-on-surface-variant">
          Unlisted variable extras are excluded and are not calculated or
          charged by MODAL.
        </p>
        {offer.priceTerms?.notes && (
          <p className="whitespace-pre-wrap">{offer.priceTerms.notes}</p>
        )}
      </section>
      {offer.attentionNeeded && (
        <p role="status" className="rounded-lg bg-amber-100 p-3 text-amber-900">
          The assigned operator needs your attention. See the activity below.
        </p>
      )}
      {offer.privateDetails && (
        <section
          className="rounded-lg bg-surface-container-low p-4 space-y-1"
          aria-label="Private handoff details"
        >
          <h3 className="font-semibold">Private handoff details</h3>
          <p>
            Passenger:{' '}
            {offer.privateDetails.passengerName ||
              'Coordinate with originating operator'}
          </p>
          <p>Pickup: {offer.privateDetails.pickupLocation}</p>
          <p>Drop-off: {offer.privateDetails.dropoffLocation}</p>
          <p>
            Origin contact:{' '}
            {[
              offer.privateDetails.operatorEmail,
              offer.privateDetails.operatorPhone,
            ]
              .filter(Boolean)
              .join(' · ') ||
              'Coordinate directly with the originating operator'}
          </p>
          {offer.isOrigin && offer.reservationId && (
            <Link
              className="text-primary underline"
              to={`/admin/reservations?reservation=${offer.reservationId}`}
            >
              Open originating reservation
            </Link>
          )}
        </section>
      )}
      {offer.isOrigin && offer.state === 'open' && (
        <section aria-label="Operator negotiations" className="space-y-3">
          <h3 className="font-semibold">
            Operator negotiations
            {offer.pendingResponseCount
              ? ` · ${offer.pendingResponseCount} need your response`
              : ''}
          </h3>
          {!offer.negotiations?.length && (
            <p className="text-sm">No operator proposals yet.</p>
          )}
          {offer.negotiations?.map((negotiation) => (
            <div
              key={negotiation.id}
              className="rounded-lg border border-outline p-3 space-y-2"
              role="region"
              aria-label={`Negotiation with ${negotiation.providerName}`}
            >
              <div className="flex flex-wrap justify-between gap-2">
                <span className="font-medium">{negotiation.providerName}</span>
                <span>
                  {negotiation.currentAmountCents
                    ? money(
                        negotiation.currentAmountCents,
                        negotiation.currency
                      )
                    : 'No current proposal'}
                </span>
              </div>
              <p className="text-sm text-on-surface-variant">
                {negotiation.state.replace(/_/g, ' ')}
              </p>
              {negotiation.state === 'awaiting_owner' && (
                <div className="flex flex-wrap gap-2">
                  {negotiationButton(
                    'confirm_agreement',
                    'Confirm agreement and handoff',
                    negotiation
                  )}
                  {negotiationButton('counter', 'Counter', negotiation, {
                    requiresAmount: true,
                  })}
                  {negotiationButton(
                    'decline_negotiation',
                    'Decline',
                    negotiation
                  )}
                </div>
              )}
              {negotiation.state === 'awaiting_owner_confirmation' && (
                <div className="flex flex-wrap gap-2">
                  {negotiationButton(
                    'confirm_agreement',
                    'Confirm agreement and handoff',
                    negotiation
                  )}
                  {negotiationButton(
                    'decline_negotiation',
                    'Decline',
                    negotiation
                  )}
                </div>
              )}
              <details>
                <summary className="cursor-pointer text-sm">
                  Proposal history ({negotiation.proposals.length})
                </summary>
                <ol className="mt-2 space-y-2 text-sm">
                  {negotiation.proposals.map((proposal) => (
                    <li key={proposal.id}>
                      {money(proposal.amountCents, proposal.currency)} ·{' '}
                      {proposal.proposedByName} ·{' '}
                      {new Date(proposal.createdAt).toLocaleString()}
                      {proposal.note && (
                        <p className="whitespace-pre-wrap">{proposal.note}</p>
                      )}
                    </li>
                  ))}
                </ol>
              </details>
            </div>
          ))}
          {!offer.negotiations?.length &&
            offer.interests
              ?.filter((interest) => interest.state === 'interested')
              .map((interest) => (
                <div
                  key={interest.companyId}
                  className="flex flex-wrap justify-between gap-2"
                >
                  <span>
                    {interest.companyName} · legacy fixed-price response
                  </span>
                  {actionButton(
                    'award',
                    `Confirm handoff to ${interest.companyName}`,
                    interest.companyId
                  )}
                </div>
              ))}
        </section>
      )}
      <div className="flex flex-wrap gap-2">
        {!offer.isOrigin && offer.state === 'open' && !offer.myNegotiation && (
          <>
            {negotiationButton('propose', 'Offer listed amount', undefined, {
              fixedAmountCents: offer.amountCents,
            })}
            {negotiationButton(
              'propose',
              'Propose different amount',
              undefined,
              {
                requiresAmount: true,
              }
            )}
            {offer.myInterest !== 'dismissed' &&
              actionButton('dismiss', 'Dismiss offer')}
          </>
        )}
        {offer.isOrigin &&
          offer.state === 'open' &&
          actionButton('withdraw', 'Withdraw offer')}
        {offer.isOrigin &&
          ['awarded', 'completion_reported'].includes(offer.state) &&
          actionButton('cancel', 'Cancel handoff')}
        {offer.isRecipient && offer.state === 'awarded' && (
          <>
            {actionButton('report_completion', 'Report completion')}
            {actionButton('attention', 'Flag a problem')}
          </>
        )}
      </div>
      {!offer.isOrigin && offer.myNegotiation && (
        <section
          className="rounded-lg border border-outline p-3 space-y-2"
          aria-label="Your negotiation"
        >
          <div className="flex flex-wrap justify-between gap-2">
            <h3 className="font-semibold">Your negotiation</h3>
            <span>
              {offer.myNegotiation.currentAmountCents
                ? money(
                    offer.myNegotiation.currentAmountCents,
                    offer.myNegotiation.currency
                  )
                : ''}
            </span>
          </div>
          <p className="text-sm">
            {offer.myNegotiation.state === 'awaiting_owner'
              ? 'Waiting for the originating operator.'
              : offer.myNegotiation.state === 'awaiting_provider'
              ? 'The originating operator sent a counter.'
              : offer.myNegotiation.state === 'awaiting_owner_confirmation'
              ? 'You accepted the counter. Final handoff confirmation remains with the originating operator.'
              : offer.myNegotiation.state.replace(/_/g, ' ')}
          </p>
          <div className="flex flex-wrap gap-2">
            {offer.myNegotiation.state === 'awaiting_provider' && (
              <>
                {negotiationButton(
                  'accept_counter',
                  'Accept counter',
                  offer.myNegotiation
                )}
                {negotiationButton(
                  'counter',
                  'Counter again',
                  offer.myNegotiation,
                  { requiresAmount: true }
                )}
                {negotiationButton(
                  'decline_negotiation',
                  'Decline',
                  offer.myNegotiation
                )}
              </>
            )}
            {[
              'awaiting_owner',
              'awaiting_provider',
              'awaiting_owner_confirmation',
            ].includes(offer.myNegotiation.state) &&
              negotiationButton(
                'withdraw_negotiation',
                'Withdraw proposal',
                offer.myNegotiation
              )}
          </div>
          <details>
            <summary className="cursor-pointer text-sm">
              Proposal history ({offer.myNegotiation.proposals.length})
            </summary>
            <ol className="mt-2 space-y-2 text-sm">
              {offer.myNegotiation.proposals.map((proposal) => (
                <li key={proposal.id}>
                  {money(proposal.amountCents, proposal.currency)} ·{' '}
                  {proposal.proposedBy === 'us' ? 'You' : 'Origin'} ·{' '}
                  {new Date(proposal.createdAt).toLocaleString()}
                  {proposal.note && (
                    <p className="whitespace-pre-wrap">{proposal.note}</p>
                  )}
                </li>
              ))}
            </ol>
          </details>
        </section>
      )}
      {offer.state === 'completion_reported' && (
        <p className="text-sm">
          The receiving operator has reported completion. The originating
          operator completes the original reservation separately.
        </p>
      )}
      {['cancelled', 'withdrawn', 'expired'].includes(offer.state) && (
        <p className="text-sm">
          This handoff is closed. The originating reservation remains with its
          operator.
        </p>
      )}
      {intent && (
        <form
          aria-label="Confirm farm-out action"
          className="rounded-lg border border-primary p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate(intent);
          }}
        >
          <h3 className="font-semibold">{intent.label}</h3>
          <p className="text-sm">
            {intent.action === 'confirm_agreement'
              ? 'This records the exact current proposal and assigns the handoff. Other provider negotiations will close. No payment is moved by MODAL.'
              : intent.action === 'accept_counter'
              ? 'Your acceptance sends this back to the originating operator for final handoff confirmation. It does not assign the reservation yet.'
              : proposalAction
              ? `Submit a private operator proposal. The originating operator retains final handoff control.${
                  intent.fixedAmountCents
                    ? ` Amount: ${money(intent.fixedAmountCents)}`
                    : ''
                }`
              : intent.action === 'award'
              ? 'Confirm this operator for the fixed amount shown. They will receive the private trip details. Other interested operators will no longer be able to accept this offer.'
              : intent.action === 'interest'
              ? 'This expresses interest at the offered amount. You are assigned only after the originating operator confirms the handoff.'
              : intent.action === 'report_completion'
              ? 'Confirm that you have fulfilled this trip. This does not charge or settle any payment.'
              : 'This change will be recorded in the handoff activity.'}
          </p>
          {intent.requiresAmount && (
            <label className="block">
              Proposal amount
              <input
                className="input-field mt-1"
                type="number"
                min="0.01"
                max="1000000"
                step="0.01"
                required
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </label>
          )}
          {proposalAction && (
            <label className="block">
              Proposal note (optional)
              <textarea
                className="input-field mt-1"
                maxLength={1000}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          )}
          {needsReason && (
            <label className="block">
              Reason
              <textarea
                className="input-field mt-1"
                required
                maxLength={1000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          )}
          {mutation.isError && (
            <p role="alert" className="text-red-700">
              {farmOutError(mutation.error)}
            </p>
          )}
          <div className="flex gap-2">
            <button
              className="btn-primary"
              disabled={
                mutation.isLoading ||
                Boolean(needsReason && !reason.trim()) ||
                Boolean(
                  intent.requiresAmount &&
                    (!Number.isFinite(Number(amount)) || Number(amount) <= 0)
                )
              }
            >
              {mutation.isLoading ? 'Saving…' : 'Confirm'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={mutation.isLoading}
              onClick={() => setIntent(null)}
            >
              Go back
            </button>
          </div>
        </form>
      )}
      {offer.events && (
        <details>
          <summary className="cursor-pointer text-sm font-medium">
            Handoff activity ({offer.events.length})
          </summary>
          <ol className="mt-3 space-y-2 text-sm">
            {offer.events.map((event, index) => (
              <li key={index} className="border-l-2 border-outline pl-3">
                <p>
                  {event.action.replace(/_/g, ' ')} · {event.companyName} ·{' '}
                  {new Date(event.createdAt).toLocaleString()}
                </p>
                {event.note && (
                  <p className="whitespace-pre-wrap">{event.note}</p>
                )}
              </li>
            ))}
          </ol>
        </details>
      )}
    </article>
  );
}
